import React, { Dispatch, KeyboardEvent, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Plus, Trash2, X, Send, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  AiConversation,
  AiDecision,
  AiItem,
  AiPending,
  AiTurn,
  aiErrorText,
  decideChat,
  deleteChat,
  listChats,
  openChat,
  sendChat,
} from '../../services/aiAssistantService';

/**
 * AiWorkspace — history · conversation · confirmation cards · composer, 85% × 85% over a blurred app.
 * Reads run at once; every change is shown on a card and runs only on Confirm, through the same API
 * (and the same permissions) as the screens. Same layout as procurement's ChatWorkspace.
 */
export interface AiStore {
  items: AiItem[]; setItems: Dispatch<SetStateAction<AiItem[]>>;
  cid: string | null; setCid: Dispatch<SetStateAction<string | null>>;
  pending: string | null; setPending: Dispatch<SetStateAction<string | null>>;
  input: string; setInput: Dispatch<SetStateAction<string>>;
  /** Called when the assistant has changed data, so the screen behind can refresh when the workspace closes. */
  onChanged: () => void;
}

const STEP_LABEL: Record<string, string> = {
  describe_operation: 'Checked what is needed for',
  call_read: 'Looked up',
  call_write: 'Change',
  get_builder: 'Opened the builder',
  propose_plan: 'Plan',
  plan_step: 'Step',
  plan_undo: 'Undid step',
};
const DECIDED: Record<string, string> = {
  confirmed: 'Confirmed', cancelled: 'Cancelled', retried: 'Resumed', 'rolled back': 'Undone', kept: 'Kept as it is',
};
const STARTERS = [
  'What can I do here?',
  'How many risk categories do we have?',
  'Show the open audit findings',
  'Create a risk category called Cyber',
  'List the active users in my entity',
  'Which compliance items are due this month?',
];
const when = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const AiWorkspace: React.FC<{ onClose: () => void; store: AiStore }> = ({ onClose, store }) => {
  const { state } = useAuth();
  const user: any = state.user || {};
  const myName: string = user.name || user.full_name || [user.first_name, user.last_name].filter(Boolean).join(' ') || 'You';
  const location = useLocation();
  const { items, setItems, cid, setCid, pending, setPending, input, setInput, onChanged } = store;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<AiConversation[]>([]);
  const [forgetting, setForgetting] = useState<AiConversation | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  const loadHistory = useCallback(() => { listChats().then(setHistory).catch(() => undefined); }, []);
  useEffect(loadHistory, [loadHistory]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [items, busy]);
  useEffect(() => { box.current?.focus(); }, [cid]);
  useEffect(() => {
    const onEsc = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape' && !forgetting) onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose, forgetting]);

  const absorb = (t: AiTurn) => {
    setCid(t.conversation_id);
    const at = t.at ?? new Date().toISOString();
    setItems((xs) => [
      ...xs,
      ...(t.reply || t.steps.length ? [{ role: 'assistant' as const, text: t.reply, steps: t.steps, at }] : []),
      ...(t.pending ? [{ role: 'pending' as const, ...t.pending, at }] : []),
    ]);
    setPending(t.pending?.summary ?? null);
    if (t.steps.some((s) => s.outcome === 'confirmed')) onChanged();
    loadHistory();
  };

  const send = async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || busy || pending) return;
    setInput('');
    setError(null);
    setItems((xs) => [...xs, { role: 'user', text: msg, at: new Date().toISOString() }]);
    setBusy(true);
    try {
      absorb(await sendChat({ conversation_id: cid, message: msg, screen: location.pathname,
        persona: user.role != null ? String(user.role) : undefined }));
    } catch (e) {
      setError(aiErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (decision: AiDecision) => {
    if (!cid) return;
    setBusy(true);
    setError(null);
    const how: Record<AiDecision, string> = { confirm: 'confirmed', cancel: 'cancelled', retry: 'retried', rollback: 'rolled back', keep: 'kept' };
    setItems((xs) => xs.map((x) => (x.role === 'pending' && !x.decided ? { ...x, decided: how[decision] } : x)));
    try {
      absorb(await decideChat(cid, decision));
    } catch (e) {
      setError(aiErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const open = async (id: string) => {
    if (busy) return;
    try {
      const c = await openChat(id);
      setCid(c.conversation_id); setItems(c.transcript); setPending(c.pending?.summary ?? null); setError(null);
    } catch (e) {
      setError(aiErrorText(e, 'That conversation could not be opened.'));
    }
  };
  const fresh = () => { setItems([]); setCid(null); setPending(null); setInput(''); setError(null); };
  const forget = async () => {
    const h = forgetting;
    setForgetting(null);
    if (!h) return;
    try {
      await deleteChat(h.id);
      if (h.id === cid) fresh();
      loadHistory();
    } catch (e) {
      setError(aiErrorText(e, 'That conversation could not be deleted.'));
    }
  };
  const key = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return createPortal(
    <div className="aiws-backdrop" data-testid="MODAL_ai_workspace">
      <div className="aiws" role="dialog" aria-modal="true" aria-label="Asking the assistant">
        <header className="aiws__head">
          <span className="aiws__badge">AI</span>
          <div className="aiws__title">
            Asking the Assistant
            <small>{myName}{user.entity_name ? ` · ${user.entity_name}` : ''}</small>
          </div>
          <button type="button" className="aiws__close" onClick={onClose} aria-label="Hide the assistant"
            data-testid="BTN_ai_workspace_close"><X size={18} /></button>
        </header>

        <div className="aiws__body">
          <aside className="aiws__side" aria-label="Conversations">
            <button type="button" className="aiws__new" onClick={fresh} disabled={busy} data-testid="BTN_ai_new_chat">
              <Plus size={15} /> New chat
            </button>
            <div className="aiws__side-title">Recent</div>
            <ul className="aiws__list">
              {history.length === 0 && <li className="aiws__empty">No conversations yet.</li>}
              {history.map((h) => (
                <li key={h.id} className="aiws__item">
                  <button type="button" className={`aiws__conv${h.id === cid ? ' is-active' : ''}`} onClick={() => open(h.id)}>
                    <span className="aiws__conv-title">{h.title}</span>
                    <span className="aiws__conv-meta">{when(h.at)}{h.waiting && ' · awaiting confirmation'}</span>
                  </button>
                  <button type="button" className="aiws__forget" onClick={() => setForgetting(h)} disabled={busy}
                    aria-label={`Delete conversation: ${h.title}`} title="Delete this conversation"><Trash2 size={13} /></button>
                </li>
              ))}
            </ul>
            <p className="aiws__note">Every change is shown to you for confirmation and runs with your own permissions.</p>
          </aside>

          <section className="aiws__main">
            <div className="aiws__scroll">
              <div className="aiws__thread">
                {items.length === 0 && (
                  <div className="aiws__hello">
                    <h2>How can I help today?</h2>
                    <p>Ask in plain words. I can look things up and create, change, delete or submit records your role allows — on any module, from any screen.</p>
                    <div className="aiws__starters">
                      {STARTERS.map((s) => (
                        <button key={s} type="button" className="aiws__starter" onClick={() => send(s)} disabled={busy}>{s}</button>
                      ))}
                    </div>
                  </div>
                )}
                {items.map((it, i) => {
                  if (it.role === 'user') {
                    return (
                      <div key={i} className="aiws__msg aiws__msg--user">
                        <div className="aiws__meta">{myName} · {when(it.at)}</div>
                        <div className="aiws__bubble">{it.text}</div>
                      </div>
                    );
                  }
                  if (it.role === 'pending') {
                    return <PendingCard key={i} item={it} busy={busy} onDecide={decide} />;
                  }
                  return (
                    <div key={i} className="aiws__msg aiws__msg--assistant">
                      <div className="aiws__meta"><span className="aiws__avatar">AI</span> Assistant · {when(it.at)}</div>
                      {it.steps.length > 0 && (
                        <div className="aiws-steps">
                          {it.steps.filter((s) => s.tool !== 'describe_operation').map((s, j) => (
                            <span key={j} className={`aiws-step aiws-step--${s.outcome}`} title={s.summary || ''}>
                              {STEP_LABEL[s.tool] ?? 'Step'} {s.noun || ''}
                              {s.outcome === 'confirmed' ? ' ✓' : s.outcome === 'denied' ? ' ✗' : s.outcome === 'cancelled' ? ' (cancelled)' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                      {it.text && (
                        <div className="aiws__answer"><ReactMarkdown remarkPlugins={[remarkGfm]}>{it.text}</ReactMarkdown></div>
                      )}
                    </div>
                  );
                })}
                {busy && (
                  <div className="aiws__msg aiws__msg--assistant">
                    <div className="aiws__meta"><span className="aiws__avatar">AI</span> Assistant</div>
                    <div className="aiws__typing"><Sparkles size={14} /> Working…</div>
                  </div>
                )}
                {error && <div className="aiws__error" role="alert">{error}</div>}
                <div ref={end} />
              </div>
            </div>

            <div className="aiws__composer">
              <div className="aiws__composer-inner">
                <textarea ref={box} className="aiws__input" rows={2} value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={key} disabled={busy || !!pending} aria-label="Message the assistant" data-testid="INP_ai_message"
                  placeholder={pending ? 'Confirm or cancel the change above first'
                    : 'Message the assistant… (Enter to send, Shift+Enter for a new line)'} />
                <button type="button" className="aiws-btn aiws-btn--primary aiws__send" onClick={() => send()}
                  disabled={busy || !!pending || !input.trim()} aria-label="Send" data-testid="BTN_ai_send"><Send size={15} /></button>
              </div>
              <div className="aiws__fine">The assistant can make mistakes; every change is confirmed by you and checked against your permissions.</div>
            </div>
          </section>
        </div>

        {forgetting && (
          <div className="aiws-dialog" role="alertdialog" aria-label="Delete conversation">
            <div className="aiws-dialog__card">
              <p>Delete “{forgetting.title}” from your conversations? It cannot be reopened. Records it created or changed stay as they are.</p>
              <div className="aiws-confirm__actions">
                <button type="button" className="aiws-btn" onClick={() => setForgetting(null)}>Keep it</button>
                <button type="button" className="aiws-btn aiws-btn--danger" onClick={forget}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/** The card waiting on the person: one change, a whole plan (numbered steps), or a plan that stopped part-way. */
const PendingCard: React.FC<{ item: AiPending & { decided?: string }; busy: boolean; onDecide: (d: AiDecision) => void }> = ({ item, busy, onDecide }) => {
  const failed = item.kind === 'plan_failed';
  const title = item.decided ? (DECIDED[item.decided] ?? item.decided)
    : failed ? 'The plan stopped part-way' : item.kind === 'plan' ? 'Confirm this plan?' : 'Confirm this change?';
  const cut = item.summary.indexOf(': ');
  const fields = !item.kind && cut > 0 ? item.summary.slice(cut + 2).split('; ') : [];
  return (
    <div className={`aiws-confirm${item.decided ? ' is-decided' : ''}${failed ? ' is-failed' : ''}`} role="group" aria-label={title}
      data-testid={item.kind ? 'CARD_ai_plan' : 'CARD_ai_change'}>
      <div className="aiws-confirm__title">{title}</div>
      <div className="aiws-confirm__summary">{item.kind ? (item.title ?? item.summary) : (cut > 0 ? item.summary.slice(0, cut) : item.summary)}</div>
      {fields.length > 0 && (
        <ul className="aiws-confirm__fields">
          {fields.map((f, j) => {
            const [k, ...v] = f.split(' = ');
            return <li key={j}><span>{k}</span><strong>{v.join(' = ')}</strong></li>;
          })}
        </ul>
      )}
      {item.steps && item.steps.length > 0 && (
        <ol className="aiws-plan">
          {item.steps.map((st) => {
            const state = failed ? (item.done?.includes(st.n) ? 'done' : st.n === item.failed ? 'failed' : 'todo') : 'todo';
            return (
              <li key={st.n} className={`aiws-plan__step is-${state}`}>
                <span className="aiws-plan__n">{state === 'done' ? '✓' : state === 'failed' ? '✗' : st.n}</span>
                <span className="aiws-plan__text">{st.summary}</span>
              </li>
            );
          })}
        </ol>
      )}
      {failed && item.error && <div className="aiws-confirm__error">{item.error}</div>}
      {!item.decided && (
        <div className="aiws-confirm__actions">
          {failed ? (
            <>
              <button type="button" className="aiws-btn" onClick={() => onDecide('keep')} disabled={busy} data-testid="BTN_ai_plan_keep">Keep as it is</button>
              <button type="button" className="aiws-btn aiws-btn--danger" onClick={() => onDecide('rollback')} disabled={busy} data-testid="BTN_ai_plan_rollback">Undo completed steps</button>
              <button type="button" className="aiws-btn aiws-btn--primary" onClick={() => onDecide('retry')} disabled={busy} data-testid="BTN_ai_plan_retry">Resume from step {item.failed}</button>
            </>
          ) : (
            <>
              <button type="button" className="aiws-btn" onClick={() => onDecide('cancel')} disabled={busy} data-testid="BTN_ai_cancel_change">Cancel</button>
              <button type="button" className="aiws-btn aiws-btn--primary" onClick={() => onDecide('confirm')} disabled={busy} data-testid="BTN_ai_confirm_change">
                {item.kind === 'plan' ? `Confirm all ${item.steps?.length ?? ''} steps` : 'Confirm'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AiWorkspace;
